import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Save, Rocket, Undo2, Redo2, Eye,
  Monitor, Tablet, Smartphone, Plus, ChevronRight, ChevronDown,
  Layers, FileText, Settings as SettingsIcon,
  Sparkles, Pencil, Check,
} from "lucide-react";

import { Toaster as SonnerToaster } from "sonner";

import { api } from "@/lib/api";

import EditableCanvas from "@/components/dashboard/inline-editor/EditableCanvas";
import SiteSettingsModal from "@/components/dashboard/inline-editor/SiteSettingsModal";
import { SRC_SECTION_OUTLINES, EDITABLE_ANCHORS } from "@/components/dashboard/inline-editor/editable-anchors";

/**
 * Phase 1 foundation for the true inline visual editor.
 *
 * Iter 76 — two-column fullscreen layout (no permanent right panel):
 *   ┌──────────────────────────────────────────────────────────────┐
 *   │ TopBar: exit · name · devices · Edit/Done · Save · Publish   │
 *   ├───────────┬──────────────────────────────────────────────────┤
 *   │  LeftBar  │       Canvas (fills all remaining space)         │
 *   │  Pages    │  Real SRC site rendered here. While Edit mode is │
 *   │  Sections │  active, a thin contextual toolbar + anchored    │
 *   │  Settings │  popover float next to the selected element.     │
 *   └───────────┴──────────────────────────────────────────────────┘
 *
 * Data flow:
 *   1. Parent (VisualEditor.jsx) passes the current `biz` doc + a reload
 *      callback (`onBusinessChange`). We fetch the exact same public-site
 *      payload the /site/<slug> route uses (`GET /api/public/site/<slug>`)
 *      so the canvas mirrors the live website 1:1.
 *   2. All edits are held in a *local* `draft` object, applied on top of
 *      the fetched payload before we pass it to the SRC shell. This lets
 *      the user see their changes live without hitting the backend on
 *      every keystroke.
 *   3. Save → PATCH /api/business/website with only the changed keys.
 *   4. Publish → auto-saves any pending diff, then POST /api/business/publish.
 *   5. After a successful save/publish the payload is refetched so both the
 *      canvas and the form show the new server state (avoids drift).
 *
 * Phase-1 selection surface (all driven by data-testids the templates
 * already ship with — see editable-anchors.js):
 *   • Hero eyebrow / title / subtitle / primary CTA / secondary CTA
 *   • Hero image
 * Later iterations extended this list with product grids, menu items,
 * service lists, and galleries. The extension point is `EDITABLE_ANCHORS`
 * in editable-anchors.js and the switch-on-type in ContextualToolbar.jsx.
 */
export default function InlineVisualEditor({ biz, onExit, onBusinessChange }) {
  const template = biz?.industry_template;
  const slug = biz?.slug;
  const bizId = biz?.business_id;

  // ─── Site data (server payload) + local draft overrides ───────────────
  const [publicPayload, setPublicPayload] = useState(null);
  const [loadingPayload, setLoadingPayload] = useState(true);
  const [draft, setDraft] = useState({}); // only-changed keys, layered on top of publicPayload.website_content
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [selection, setSelection] = useState(null); // { anchor, elementRect }
  const [deviceMode, setDeviceMode] = useState("desktop"); // desktop | tablet | mobile
  const [siteSettingsOpen, setSiteSettingsOpen] = useState(false);
  // Iter 76 — explicit Edit mode. The canvas is a clean preview until the
  // user presses Edit; pressing Done clears the selection and all editing
  // chrome so the site returns to a clean preview state.
  const [isEditMode, setIsEditMode] = useState(false);
  const toggleEditMode = useCallback(() => {
    setIsEditMode((prev) => {
      if (prev) setSelection(null);
      return !prev;
    });
  }, []);

  // Escape deselects the current element.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setSelection(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // ─── Active page for the canvas ──────────────────────────────────────
  // Which page in `biz.pages` the canvas is currently rendering. `null`
  // means "use the home page" — resolved below. Clicking a page in the
  // LeftPanel Pages tab sets this, which:
  //   1. Swaps the canvas to render the matching template Page component.
  //   2. Clears the current selection so the toolbar doesn't keep
  //      showing an anchor from the previous page.
  const [activePageSlug, setActivePageSlug] = useState(null);
  const homePage = useMemo(() => biz?.pages?.find((p) => p.is_home) || (biz?.pages?.[0] ?? null), [biz]);
  const activePage = useMemo(() => {
    if (!activePageSlug) return homePage;
    return biz?.pages?.find((p) => p.slug === activePageSlug) || homePage;
  }, [biz, activePageSlug, homePage]);
  // Iter 77 — virtual template pages (service Consultations/Reviews) exist
  // even when absent from `biz.pages`, so trust the requested slug for the
  // canvas while `activePage` (metadata/sections) falls back to home.
  const resolvedPageSlug = activePageSlug || activePage?.slug || "";
  const selectPage = useCallback((slug) => {
    setActivePageSlug(slug || null);
    setSelection(null);
  }, []);

  // Undo / redo history — we track lengths as state (not just refs) so
  // the Undo/Redo buttons re-render correctly when their disabled state
  // changes. The actual draft stack lives on `historyRef` to keep the
  // memory in one place.
  const historyRef = useRef({ past: [], future: [] });
  const [historyTick, setHistoryTick] = useState(0);
  const bumpHistory = () => setHistoryTick((t) => t + 1);
  const pushHistory = useCallback((prevDraft) => {
    historyRef.current.past.push(prevDraft);
    if (historyRef.current.past.length > 50) historyRef.current.past.shift();
    historyRef.current.future = [];
    bumpHistory();
  }, []);

  const undo = useCallback(() => {
    const { past, future } = historyRef.current;
    if (past.length === 0) return;
    const prev = past.pop();
    future.push(draft);
    setDraft(prev);
    bumpHistory();
  }, [draft]);

  const redo = useCallback(() => {
    const { past, future } = historyRef.current;
    if (future.length === 0) return;
    const next = future.pop();
    past.push(draft);
    setDraft(next);
    bumpHistory();
  }, [draft]);

  // Consume historyTick so React knows this render depends on it.
  void historyTick;

  const loadPayload = useCallback(async () => {
    if (!slug) return;
    setLoadingPayload(true);
    try {
      const { data } = await api.get(`/public/site/${slug}`);
      setPublicPayload(data);
      setDraft({});
      historyRef.current = { past: [], future: [] };
    } catch (e) {
      toast.error("Couldn't load website preview");
    } finally {
      setLoadingPayload(false);
    }
  }, [slug]);

  useEffect(() => { loadPayload(); }, [loadPayload]);

  // ─── Draft mutation ──────────────────────────────────────────────────
  const updateField = useCallback((path, value) => {
    setDraft((current) => {
      pushHistory(current);
      // Iter-75 — copy-override edits arrive as `path = "copy_overrides.<copyPath>"`
      // (e.g. `"copy_overrides.home.servicesOverview.title"`). We merge the new
      // value into the FULL existing override dict (draft first, then WC) so
      // the eventual PATCH sends a complete map — never wiping unrelated
      // customer overrides.
      if (typeof path === "string" && path.startsWith("copy_overrides.")) {
        const copyKey = path.slice("copy_overrides.".length);
        const base = current.copy_overrides
          ?? publicPayload?.website_content?.copy_overrides
          ?? {};
        return { ...current, copy_overrides: { ...base, [copyKey]: value } };
      }
      return { ...current, [path]: value };
    });
  }, [pushHistory, publicPayload]);

  const dirty = Object.keys(draft).length > 0;

  const effectivePayload = useMemo(() => {
    // Overlay the local draft on top of the fetched website_content so the
    // canvas mirrors what the user has edited in this session.
    if (!publicPayload) return null;
    const wc = { ...(publicPayload.website_content || {}), ...draft };

    // Iter-75 — apply the merged (WC + draft) `copy_overrides` map onto
    // whichever nested copy tree the template consumes, so the live
    // canvas shows the user's heading edits before they hit Save.
    // Mirrors the backend's `_apply_copy_overrides` helper — we deep-walk
    // dot-notation paths and set the terminal string.
    const mergedOverrides = {
      ...(publicPayload.website_content?.copy_overrides || {}),
      ...(draft.copy_overrides || {}),
    };
    const applyOverridesToTree = (tree) => {
      if (!tree || Object.keys(mergedOverrides).length === 0) return tree;
      const cloned = JSON.parse(JSON.stringify(tree));
      for (const [path, value] of Object.entries(mergedOverrides)) {
        if (typeof value !== "string") continue;
        const parts = String(path).split(".").filter(Boolean);
        if (!parts.length) continue;
        let node = cloned;
        for (let i = 0; i < parts.length - 1; i++) {
          const p = parts[i];
          if (!node[p] || typeof node[p] !== "object") node[p] = {};
          node = node[p];
        }
        node[parts[parts.length - 1]] = value;
      }
      return cloned;
    };

    // The 3 SRC templates each read from a different derived config, but
    // all trace back to the same underlying website_content fields. We
    // apply the current draft to every downstream config so the canvas
    // updates live as the user types (no round trip required).

    // Service template — reads `service_config.copy.home.hero.{title,subtitle,eyebrow}`
    // and `service_config.images.hero`.
    const applyToServiceConfig = (cfg) => {
      if (!cfg) return cfg;
      const nextCopy = applyOverridesToTree(cfg.copy || {}) || {};
      nextCopy.home = nextCopy.home || {};
      nextCopy.home.hero = nextCopy.home.hero || {};
      if (draft.hero_title !== undefined)    nextCopy.home.hero.title    = draft.hero_title;
      if (draft.hero_subtitle !== undefined) nextCopy.home.hero.subtitle = draft.hero_subtitle;
      if (draft.hero_eyebrow !== undefined)  nextCopy.home.hero.eyebrow  = draft.hero_eyebrow;
      const nextImages = { ...(cfg.images || {}) };
      if (draft.hero_image_url !== undefined) nextImages.hero = draft.hero_image_url;
      return { ...cfg, copy: nextCopy, images: nextImages };
    };

    // Ecommerce template — reads `ecommerce_config.hero.{headline1,headline2,headline3,subhead,image,ctaPrimary,ctaSecondary}`.
    const applyToEcomConfig = (cfg) => {
      if (!cfg) return cfg;
      const nextHero = { ...(cfg.hero || {}) };
      if (draft.hero_title !== undefined) { nextHero.headline1 = draft.hero_title; nextHero.title = draft.hero_title; }
      if (draft.hero_subtitle !== undefined) { nextHero.subhead = draft.hero_subtitle; nextHero.subtitle = draft.hero_subtitle; }
      if (draft.hero_eyebrow !== undefined) nextHero.eyebrow = draft.hero_eyebrow;
      if (draft.hero_image_url !== undefined) nextHero.image = draft.hero_image_url;
      if (draft.hero_cta_text !== undefined) nextHero.ctaPrimary = draft.hero_cta_text;
      if (draft.hero_cta2_text !== undefined) nextHero.ctaSecondary = draft.hero_cta2_text;
      // Apply copy_overrides onto the ecom copy tree too (defensive — most
      // ecom headings use canonical scalar fields; overrides only cover
      // gaps).
      const nextCopy = applyOverridesToTree(cfg.copy || {}) || cfg.copy;
      return { ...cfg, hero: nextHero, copy: nextCopy };
    };

    // Restaurant template — reads `restaurant_config.hero.{title,titleEm,subtitle,image}`.
    // Restaurant's config has TOP-LEVEL section blocks (menu_intro, about,
    // contact, reservations) — so overrides apply to the whole cfg dict,
    // mirroring the backend's `_apply_copy_overrides` call site.
    const applyToRestaurantConfig = (cfg) => {
      if (!cfg) return cfg;
      const withOverrides = applyOverridesToTree(cfg) || cfg;
      const nextHero = { ...(withOverrides.hero || {}) };
      if (draft.hero_title !== undefined) nextHero.title = draft.hero_title;
      if (draft.hero_subtitle !== undefined) nextHero.subtitle = draft.hero_subtitle;
      if (draft.hero_image_url !== undefined) nextHero.image = draft.hero_image_url;
      return { ...withOverrides, hero: nextHero };
    };

    return {
      ...publicPayload,
      website_content: wc,
      service_config: applyToServiceConfig(publicPayload.service_config),
      ecommerce_config: applyToEcomConfig(publicPayload.ecommerce_config),
      restaurant_config: applyToRestaurantConfig(publicPayload.restaurant_config),
    };
  }, [publicPayload, draft]);

  // ─── Save / Publish ─────────────────────────────────────────────────
  const save = async () => {
    if (!dirty) return;
    setSaving(true);
    try {
      await api.patch(`/business/website?business_id=${bizId}`, draft);
      toast.success("Changes saved.");
      // Refetch to pick up derived config changes + reset draft.
      await loadPayload();
      onBusinessChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save changes");
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    setPublishing(true);
    try {
      if (dirty) {
        await api.patch(`/business/website?business_id=${bizId}`, draft);
      }
      await api.post(`/business/publish?business_id=${bizId}`, {});
      toast.success("Your changes are now live.");
      await loadPayload();
      onBusinessChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't publish");
    } finally {
      setPublishing(false);
    }
  };

  // Save an entire list-typed website_content field (menu, services, gallery)
  // in a single PATCH. Used by the Phase 2 collection editors so the whole
  // list persists as one atomic write instead of per-item PATCHes.
  const saveWebsiteContentList = async (patch) => {
    await api.patch(`/business/website?business_id=${bizId}`, patch);
    await loadPayload();
    onBusinessChange?.();
  };

  // ─── Render ─────────────────────────────────────────────────────────
  if (loadingPayload || !publicPayload) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white z-50" data-testid="inline-editor-loading">
        <Loader2 className="w-8 h-8 animate-spin text-gray-500" />
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-[#F5F5F2] z-50 flex flex-col" data-testid="inline-visual-editor">
      <TopBar
        biz={biz}
        deviceMode={deviceMode}
        setDeviceMode={setDeviceMode}
        onExit={onExit}
        onUndo={undo}
        onRedo={redo}
        canUndo={historyRef.current.past.length > 0}
        canRedo={historyRef.current.future.length > 0}
        onSave={save}
        onPublish={publish}
        dirty={dirty}
        saving={saving}
        publishing={publishing}
        slug={slug}
        onOpenSettings={() => setSiteSettingsOpen(true)}
        isEditMode={isEditMode}
        onToggleEditMode={toggleEditMode}
      />

      <div className="flex-1 flex overflow-hidden">
        <LeftPanel
          biz={biz}
          template={template}
          activePageSlug={resolvedPageSlug}
          onSelectPage={selectPage}
          onEnsureEditMode={() => setIsEditMode(true)}
          onSelectAnchor={(anchor) => { setIsEditMode(true); setSelection({ anchor, elementRect: null }); }}
        />

        <EditableCanvas
          payload={effectivePayload}
          template={template}
          slug={slug}
          pageSlug={resolvedPageSlug}
          deviceMode={deviceMode}
          selection={selection}
          setSelection={setSelection}
          isEditMode={isEditMode}
          onDeselect={() => setSelection(null)}
          editorProps={{
            draft,
            publicPayload,
            updateField,
            bizId,
            onCanvasChange: loadPayload,
            saveWebsiteContentList,
          }}
        />
      </div>

      {siteSettingsOpen && (
        <SiteSettingsModal
          bizId={bizId}
          biz={biz}
          publicPayload={publicPayload}
          onClose={() => setSiteSettingsOpen(false)}
          refresh={async () => { await loadPayload(); onBusinessChange?.(); }}
        />
      )}

      {/* Ensure sonner toasts render above the editor's `fixed inset-0 z-50`
          wrapper so save/publish feedback is visible to the user. */}
      <SonnerToaster
        position="top-right"
        richColors
        theme="light"
        style={{ zIndex: 100 }}
      />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Top Bar                                                                   */
/* -------------------------------------------------------------------------- */

function TopBar({
  biz, deviceMode, setDeviceMode, onExit, onUndo, onRedo, canUndo, canRedo,
  onSave, onPublish, dirty, saving, publishing, slug, onOpenSettings,
  isEditMode, onToggleEditMode,
}) {
  return (
    <div className="h-14 bg-white border-b-2 border-black flex items-center gap-3 px-3 flex-shrink-0" data-testid="inline-editor-topbar">
      {/* Left cluster */}
      <button
        onClick={onExit}
        className="w-9 h-9 rounded-lg border-2 border-black bg-white hover:bg-gray-50 flex items-center justify-center"
        title="Exit editor"
        data-testid="inline-editor-exit"
      >
        <ArrowLeft className="w-4 h-4" />
      </button>
      <div className="hidden md:flex items-center gap-2 min-w-0 max-w-[220px]">
        <div className="w-9 h-9 rounded-lg bg-black text-white flex items-center justify-center font-black text-xs">
          {(biz?.summary?.name || biz?.name || "?")?.slice(0, 2).toUpperCase()}
        </div>
        <div className="min-w-0">
          <div className="text-sm font-bold truncate">{biz?.summary?.name || biz?.name || "Website"}</div>
          <div className="text-[11px] text-gray-500 truncate">/site/{slug}</div>
        </div>
      </div>
      <div className="w-px h-6 bg-gray-200" />
      <div className="hidden md:flex items-center gap-1 px-2 py-1 rounded-lg bg-gray-100 text-xs font-semibold text-gray-700">
        <span>Home</span>
      </div>

      {/* Center cluster: device switcher */}
      <div className="flex-1 flex justify-center">
        <div className="flex items-center gap-1 bg-gray-100 rounded-full p-1">
          <DeviceBtn active={deviceMode === "desktop"} onClick={() => setDeviceMode("desktop")} icon={Monitor} label="Desktop" testId="inline-editor-device-desktop" />
          <DeviceBtn active={deviceMode === "tablet"}  onClick={() => setDeviceMode("tablet")}  icon={Tablet}  label="Tablet"  testId="inline-editor-device-tablet" />
          <DeviceBtn active={deviceMode === "mobile"}  onClick={() => setDeviceMode("mobile")}  icon={Smartphone} label="Mobile" testId="inline-editor-device-mobile" />
        </div>
      </div>

      {/* Edit / Done mode toggle */}
      <button
        onClick={onToggleEditMode}
        className={`inline-flex items-center gap-1.5 px-4 h-9 rounded-lg border-2 border-black text-sm font-black ${
          isEditMode ? "bg-black text-white hover:bg-gray-800" : "bg-white hover:bg-gray-50"
        }`}
        title={isEditMode ? "Finish editing" : "Start editing"}
        data-testid="inline-editor-edit-toggle"
      >
        {isEditMode ? <Check className="w-4 h-4" /> : <Pencil className="w-4 h-4" />}
        {isEditMode ? "Done" : "Edit"}
      </button>

      {/* Right cluster */}
      <button
        onClick={onUndo}
        disabled={!canUndo}
        className="w-9 h-9 rounded-lg border-2 border-black bg-white hover:bg-gray-50 flex items-center justify-center disabled:opacity-40"
        title="Undo"
        data-testid="inline-editor-undo"
      >
        <Undo2 className="w-4 h-4" />
      </button>
      <button
        onClick={onRedo}
        disabled={!canRedo}
        className="w-9 h-9 rounded-lg border-2 border-black bg-white hover:bg-gray-50 flex items-center justify-center disabled:opacity-40"
        title="Redo"
        data-testid="inline-editor-redo"
      >
        <Redo2 className="w-4 h-4" />
      </button>
      <a
        href={`/site/${slug}`}
        target="_blank"
        rel="noreferrer"
        className="hidden md:inline-flex items-center gap-1.5 px-3 h-9 rounded-lg border-2 border-black bg-white hover:bg-gray-50 text-sm font-bold"
        data-testid="inline-editor-preview"
      >
        <Eye className="w-4 h-4" /> Preview
      </a>
      <button
        onClick={onOpenSettings}
        className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg border-2 border-black bg-white text-sm font-bold hover:bg-gray-50"
        title="Site settings"
        data-testid="inline-editor-site-settings"
      >
        <SettingsIcon className="w-4 h-4" />
        <span className="hidden md:inline">Site Settings</span>
      </button>
      <button
        onClick={onSave}
        disabled={!dirty || saving}
        className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg border-2 border-black bg-white text-sm font-bold hover:bg-gray-50 disabled:opacity-50"
        data-testid="inline-editor-save"
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
        {dirty ? "Save Changes" : "Saved"}
      </button>
      <button
        onClick={onPublish}
        disabled={publishing}
        className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg bg-emerald-500 text-white text-sm font-black hover:bg-emerald-600 disabled:opacity-50"
        data-testid="inline-editor-publish"
      >
        {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
        Publish
      </button>
    </div>
  );
}

function DeviceBtn({ active, onClick, icon: Icon, label, testId }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold ${active ? "bg-white shadow-sm" : "text-gray-600 hover:text-black"}`}
      data-testid={testId}
    >
      <Icon className="w-4 h-4" /> <span className="hidden lg:inline">{label}</span>
    </button>
  );
}

/* -------------------------------------------------------------------------- */
/*  Left Panel                                                                */
/* -------------------------------------------------------------------------- */

/**
 * Iter 77 — the editor's page list mirrors what a visitor can actually
 * reach:
 *  • service    → biz pages + the always-routable Consultations / Reviews
 *                 template pages (reachable via nav/CTAs even when absent
 *                 from `pages[]`).
 *  • restaurant → a single-page site; fake per-page entries that all render
 *                 the same landing are not shown.
 */
function buildEditorPages(biz, template) {
  const pages = biz?.pages || [];
  if (template === "restaurant") {
    const home = pages.find((p) => p.is_home || p.slug === "home" || !p.slug);
    return [home || { page_id: "home", slug: "home", title: "Home", is_home: true }];
  }
  if (template === "service") {
    const out = [...pages];
    for (const extra of [
      { slug: "consultations", title: "Consultations" },
      { slug: "reviews", title: "Reviews" },
    ]) {
      if (!out.some((p) => p.slug === extra.slug)) {
        out.push({ page_id: `virtual-${extra.slug}`, slug: extra.slug, title: extra.title });
      }
    }
    return out;
  }
  return pages;
}

function LeftPanel({ biz, template, activePageSlug, onSelectPage, onSelectAnchor, onEnsureEditMode }) {
  const [openSection, setOpenSection] = useState({ pages: true, sections: true, settings: false });
  const outline = SRC_SECTION_OUTLINES[template] || [];

  const scrollTo = (testid) => {
    // The site-header entry always scrolls the canvas to the very top —
    // the SRC template's header is `position: sticky/fixed` so
    // scrollIntoView on it does nothing useful. Other outline entries
    // still scroll to the anchored testid element.
    if (testid === "site-header") {
      const scroller = document.querySelector('[data-testid="inline-editor-canvas"] > div');
      if (scroller?.scrollTo) scroller.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    const el = document.querySelector(`[data-testid="${testid}"]`);
    if (el?.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <aside className="w-64 flex-shrink-0 border-r-2 border-black bg-white overflow-y-auto" data-testid="inline-editor-left-panel">
      {/* Pages */}
      <PanelGroup
        icon={FileText}
        label="Pages"
        open={openSection.pages}
        onToggle={() => setOpenSection((o) => ({ ...o, pages: !o.pages }))}
        addAction={() => window.dispatchEvent(new CustomEvent("visualeditor:goto", { detail: "pages" }))}
      >
        {buildEditorPages(biz, template).map((p) => {
          const isActive = activePageSlug ? p.slug === activePageSlug : p.is_home;
          return (
            <button
              key={p.page_id}
              onClick={() => onSelectPage?.(p.slug)}
              className={`w-full text-left px-3 py-1.5 text-sm rounded-md flex items-center gap-2 ${isActive ? "bg-emerald-50 text-emerald-900 font-bold" : "text-gray-700 hover:bg-gray-100"}`}
              data-testid={`inline-editor-page-${p.slug || "home"}`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${isActive ? "bg-emerald-500" : "bg-gray-300"}`} />
              {p.nav_label || p.title || (p.is_home ? "Home" : p.slug)}
            </button>
          );
        })}
      </PanelGroup>

      {/* Section Outline */}
      <PanelGroup
        icon={Layers}
        label="Section Outline"
        open={openSection.sections}
        onToggle={() => setOpenSection((o) => ({ ...o, sections: !o.sections }))}
      >
        {outline.map((s) => (
          <div key={s.testid} className="group flex items-center gap-0.5">
            <button
              onClick={() => scrollTo(s.testid)}
              className="flex-1 text-left px-3 py-1.5 text-sm rounded-md flex items-center gap-2 text-gray-700 hover:bg-gray-100 min-w-0"
              data-testid={`inline-editor-outline-${s.testid}`}
            >
              <span className="w-4 text-gray-400">≡</span>
              <span className="truncate">{s.label}</span>
            </button>
            {s.testid !== "site-header" && s.testid !== "site-footer" && (
              <button
                onClick={() => {
                  // Enter Edit mode first, then fire a real DOM click on the
                  // element so the normal selection pipeline runs. When the
                  // DOM element is missing (e.g. the FAQ list on the service
                  // Home page which doesn't render <FAQSection>), fall back
                  // to a synthetic selection so the user can still edit the
                  // site-wide list.
                  onEnsureEditMode?.();
                  const el = document.querySelector(`[data-testid="${s.testid}"]`);
                  if (el) {
                    setTimeout(() => {
                      el.dispatchEvent(new MouseEvent("click", { bubbles: true, clientX: 0, clientY: 0 }));
                    }, 80);
                    return;
                  }
                  const anchor = EDITABLE_ANCHORS.find((a) => a.testid === s.testid);
                  if (anchor) onSelectAnchor?.(anchor);
                }}
                className="px-2 py-1 rounded-md text-[10px] font-bold text-gray-500 opacity-0 group-hover:opacity-100 hover:text-black hover:bg-gray-100 transition-opacity"
                title="Edit this section's settings"
                data-testid={`inline-editor-outline-edit-${s.testid}`}
              >
                Edit
              </button>
            )}
            {(s.testid === "site-header" || s.testid === "site-footer") && (
              <button
                onClick={() => {
                  onEnsureEditMode?.();
                  const el = document.querySelector(`[data-testid="${s.testid}"]`);
                  if (el) {
                    setTimeout(() => {
                      el.dispatchEvent(new MouseEvent("click", { bubbles: true, clientX: 0, clientY: 0 }));
                    }, 80);
                    return;
                  }
                  const anchor = EDITABLE_ANCHORS.find((a) => a.testid === s.testid);
                  if (anchor) onSelectAnchor?.(anchor);
                }}
                className="px-2 py-1 rounded-md text-[10px] font-bold text-gray-500 opacity-0 group-hover:opacity-100 hover:text-black hover:bg-gray-100 transition-opacity"
                title={s.testid === "site-header" ? "Edit header settings" : "Edit footer settings"}
                data-testid={`inline-editor-outline-edit-${s.testid}`}
              >
                Edit
              </button>
            )}
          </div>
        ))}
      </PanelGroup>

      {/* Website Settings */}
      <PanelGroup
        icon={SettingsIcon}
        label="Website Settings"
        open={openSection.settings}
        onToggle={() => setOpenSection((o) => ({ ...o, settings: !o.settings }))}
      >
        <SidebarLink label="General"      onClick={() => window.dispatchEvent(new CustomEvent("visualeditor:goto", { detail: "settings" }))} testId="inline-editor-settings-general" />
        <SidebarLink label="Branding"     onClick={() => window.dispatchEvent(new CustomEvent("visualeditor:goto", { detail: "settings" }))} testId="inline-editor-settings-branding" />
        <SidebarLink label="SEO & Social" onClick={() => window.dispatchEvent(new CustomEvent("visualeditor:goto", { detail: "settings" }))} testId="inline-editor-settings-seo" />
        <SidebarLink label="Domains"      onClick={() => window.dispatchEvent(new CustomEvent("visualeditor:goto", { detail: "domain" }))}   testId="inline-editor-settings-domains" />
        <SidebarLink label="Integrations" onClick={() => window.dispatchEvent(new CustomEvent("visualeditor:goto", { detail: "settings" }))} testId="inline-editor-settings-integrations" />
        <SidebarLink label="Analytics"    onClick={() => window.dispatchEvent(new CustomEvent("visualeditor:goto", { detail: "analytics" }))} testId="inline-editor-settings-analytics" />
      </PanelGroup>

      {/* "Everything is editable" tip */}
      <div className="p-3 mt-2">
        <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-[11px] text-emerald-900" data-testid="inline-editor-everything-badge">
          <div className="flex items-center gap-1.5 font-black mb-1">
            <Sparkles className="w-3.5 h-3.5" /> Everything is editable
          </div>
          <p className="leading-relaxed">Press <b>Edit</b> in the top bar, then click any headline, image, or button on the canvas to edit it in place.</p>
        </div>
      </div>
    </aside>
  );
}

function PanelGroup({ icon: Icon, label, open, onToggle, addAction, children }) {
  return (
    <div className="border-b border-gray-100">
      <div className="flex items-center gap-2 px-3 py-2.5">
        <button onClick={onToggle} className="flex-1 flex items-center gap-2 text-left" data-testid={`inline-editor-panel-toggle-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
          {open ? <ChevronDown className="w-3.5 h-3.5 text-gray-400" /> : <ChevronRight className="w-3.5 h-3.5 text-gray-400" />}
          <Icon className="w-4 h-4 text-gray-500" />
          <span className="text-[11px] font-black uppercase tracking-widest text-gray-700">{label}</span>
        </button>
        {addAction && (
          <button onClick={addAction} className="w-5 h-5 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center">
            <Plus className="w-3 h-3" />
          </button>
        )}
      </div>
      {open && <div className="px-2 pb-2 space-y-0.5">{children}</div>}
    </div>
  );
}

function SidebarLink({ label, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      className="w-full text-left px-3 py-1.5 text-sm rounded-md text-gray-700 hover:bg-gray-100 flex items-center justify-between gap-2"
      data-testid={testId}
    >
      {label}
      <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
    </button>
  );
}
